import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/auth-context'

const origin = process.env.NODE_ENV === 'development' ? 'http://localhost:3050' : 'https://api.affdash.nordpar.com'

async function publicPost(path: string, body: object) {
    return fetch(origin + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    })
}

function PublicCard({ title, children }: { title: string, children: React.ReactNode }) {
    return <div className="container d-flex justify-content-center align-items-center min-vh-100">
        <Card className="shadow-sm border-0 w-100" style={{ maxWidth: 520 }}>
            <Card.Body className="p-4 p-md-5"><h3 className="mb-4">{title}</h3>{children}</Card.Body>
        </Card>
    </div>
}

function hashToken(hash: string) {
    return new URLSearchParams(hash.replace(/^#/, '')).get('token') || ''
}

export function AcceptInvitation() {
    const location = useLocation()
    const navigate = useNavigate()
    const { login } = useAuth()
    const [token] = useState(() => hashToken(location.hash))
    const [details, setDetails] = useState<any>(null)
    const [password, setPassword] = useState('')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(true)
    const [accepted, setAccepted] = useState(false)

    useEffect(() => {
        if (token && window.location.hash) window.history.replaceState(null, '', location.pathname + location.search)
    }, [location.pathname, location.search, token])

    useEffect(() => {
        publicPost('/user/invitations/inspect', { token })
            .then(async response => response.ok ? response.json() : Promise.reject(await response.text()))
            .then(setDetails)
            .catch(value => setError(String(value || 'Invitation is invalid or has expired.')))
            .finally(() => setLoading(false))
    }, [token])

    const accept = async (event: React.FormEvent) => {
        event.preventDefault()
        setLoading(true)
        setError('')
        try {
            const response = await publicPost('/user/invitations/accept', { token, password })
            if (!response.ok) throw new Error(await response.text())
            const body = await response.json()
            if (body.token) {
                login(body.token)
                navigate('/')
            } else {
                setAccepted(true)
            }
        } catch (value) {
            setError(value instanceof Error ? value.message : 'Could not accept the invitation.')
        } finally {
            setLoading(false)
        }
    }

    return <PublicCard title="Accept invitation">
        {accepted ? <Alert variant="success">Access added. <Link to="/login">Log in with your existing e-mail and password</Link> to continue.</Alert>
        : loading && !details ? <Spinner animation="border" /> : error ? <Alert variant="danger">{error}</Alert> : details && <Form onSubmit={accept}>
            <p>You have been invited to <strong>{details.account}</strong> as <strong>{details.role}</strong>.</p>
            <Form.Group className="mb-3" controlId="invitation-email"><Form.Label>E-mail</Form.Label><Form.Control value={details.email} disabled /></Form.Group>
            {details.requiresPassword && <Form.Group className="mb-3" controlId="invitation-password">
                <Form.Label>Choose a password</Form.Label>
                <Form.Control type="password" minLength={8} required value={password} onChange={event => setPassword(event.target.value)} autoComplete="new-password" />
                <Form.Text>Use at least 8 characters.</Form.Text>
            </Form.Group>}
            <Button type="submit" disabled={loading}>{loading ? 'Accepting…' : 'Accept invitation'}</Button>
        </Form>}
    </PublicCard>
}

export function ForgotPassword() {
    const location = useLocation()
    const suggestedEmail = typeof location.state?.email === 'string' ? location.state.email : ''
    const [email, setEmail] = useState(suggestedEmail)
    const [sent, setSent] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const submit = async (event: React.FormEvent) => {
        event.preventDefault()
        setLoading(true)
        setError('')
        try {
            const response = await publicPost('/user/password-reset/request', { email })
            if (!response.ok) throw new Error('Request failed')
            setSent(true)
        } catch {
            setError('We could not process the request. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    return <PublicCard title="Reset password">
        {sent ? <>
            <Alert variant="success" aria-live="polite">If an account exists for that e-mail, a reset link has been sent.</Alert>
            <p className="text-muted small mb-0">Please check your inbox and spam folder. The link expires after one hour.</p>
        </> : <Form onSubmit={submit}>
            <p className="text-muted">Enter the e-mail address you use to sign in. We’ll send instructions if a matching account exists.</p>
            {error && <Alert variant="danger" aria-live="polite">{error}</Alert>}
            <Form.Group className="mb-3" controlId="reset-email">
                <Form.Label>E-mail</Form.Label>
                <Form.Control type="email" required value={email} onChange={event => setEmail(event.target.value)} autoComplete="email" autoFocus disabled={loading} />
            </Form.Group>
            <Button type="submit" className="w-100" disabled={loading}>{loading ? <><Spinner animation="border" size="sm" className="me-2" />Sending…</> : 'Send reset link'}</Button>
        </Form>}
        <div className="mt-3"><Link to="/login">Back to login</Link></div>
    </PublicCard>
}

export function CompletePasswordReset() {
    const location = useLocation()
    const [token] = useState(() => hashToken(location.hash))
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [error, setError] = useState('')
    const [complete, setComplete] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (token && window.location.hash) window.history.replaceState(null, '', location.pathname + location.search)
    }, [location.pathname, location.search, token])

    const submit = async (event: React.FormEvent) => {
        event.preventDefault()
        if (password !== confirmPassword) {
            setError('Passwords do not match.')
            return
        }
        setLoading(true)
        setError('')
        try {
            const response = await publicPost('/user/password-reset/complete', { token, password })
            if (!response.ok) throw new Error(await response.text())
            setComplete(true)
        } catch (value) {
            setError(value instanceof Error ? value.message : 'Could not reset the password.')
        } finally {
            setLoading(false)
        }
    }

    return <PublicCard title="Choose a new password">
        {!token ? <>
            <Alert variant="danger">This reset link is invalid or has expired.</Alert>
            <Link to="/forgot-password">Request a new reset link</Link>
        </> : complete ? <Alert variant="success">Password updated. <Link to="/login">Log in</Link></Alert> : <Form onSubmit={submit}>
            {error && <Alert variant="danger">{error}</Alert>}
            <p className="text-muted">Choose a unique password with at least 8 characters.</p>
            <Form.Group className="mb-3" controlId="reset-password">
                <Form.Label>New password</Form.Label>
                <Form.Control type="password" required minLength={8} value={password} onChange={event => setPassword(event.target.value)} autoComplete="new-password" autoFocus disabled={loading} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="reset-password-confirmation">
                <Form.Label>Confirm new password</Form.Label>
                <Form.Control type="password" required minLength={8} value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} autoComplete="new-password" disabled={loading} />
            </Form.Group>
            <Button type="submit" className="w-100" disabled={loading}>{loading ? <><Spinner animation="border" size="sm" className="me-2" />Updating…</> : 'Update password'}</Button>
        </Form>}
    </PublicCard>
}
